import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor as MonacoEditor } from 'monaco-editor';
import * as monaco from 'monaco-editor';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import NumberBox from 'devextreme-react/number-box';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { custom } from 'devextreme/ui/dialog';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import {
  fileAccessRestrictedHref,
  isFileAccessRestrictedStatus
} from '../storageAccess/fileAccessRestrictedUrls';
import { useShellToolbarCenter } from '../shell/ShellToolbarContext';
import { showErrorToast, showSuccessToast, showWarningToast } from '../notifications/toast';
import { languageFromPath } from '../textEditor/languageFromPath';
import { formatJson, formatXml } from '../textEditor/formatEditorContent';
import {
  ApiHttpError,
  ContentRevisionConflictError,
  copyPathForConflict,
  fetchContent,
  saveContent,
  type StorageContentResponse
} from '../textEditor/textEditorApi';
import {
  decryptEnvelope,
  decryptEnvelopeWithRawKey,
  detectEncryptedContent,
  encryptPlaintext,
  exportContentKeyBytes,
  contentKeyFromBase64,
  contentKeyToBase64,
  parseEnvelope,
  withManagedWrappedKey
} from '../textEditor/editorEncryption';
import {
  fetchEditorCryptoStatus,
  managedUnwrapDataKey,
  managedWrapDataKey,
  type EditorCryptoStatus
} from '../textEditor/editorCryptoApi';
import { JencUnlockDialog } from '../textEditor/JencUnlockDialog';
import {
  resolveJencLockKind,
  resolveUnlockCancelLoadState,
  type JencLockKind
} from '../textEditor/jencUnlockState';
import { getEditorAutosaveIntervalMs } from '../editorShell/editorAutosavePreference';
import {
  isEmbeddedEditor,
  resolveEditorChromeMode,
  type EditorChromeMode
} from '../editorShell/editorChrome';
import { MinimalEditorChrome } from '../editorShell/MinimalEditorChrome';
import { ensureMonacoReady } from '../textEditor/monacoSetup';
import {
  describeSupport,
  getLanguageCapability,
  listLanguageCapabilities
} from '../codeEditor/languageCapabilityRegistry';
import { formatCodeContent } from '../codeEditor/formatCodeContent';
import {
  toMonacoMarkerSeverity,
  validateCodeContent,
  type CodeDiagnostic
} from '../codeEditor/validateCodeContent';
import {
  getEffectiveOptions,
  loadCodeEditorPreferences,
  saveCodeEditorPreferences,
  type CodeEditorPreferences
} from '../codeEditor/codeEditorPreferences';
import {
  fetchCodeLanguageServiceHealth,
  shouldUseMonacoOnlyFallback,
  type LanguageServiceHealthDto
} from '../codeEditor/lsp/codeLanguageServiceClient';
import { CodeEditorCreatePanel } from '../codeEditor/CodeEditorCreatePanel';
import { TextEditorCreatePanel } from '../textEditor/TextEditorCreatePanel';
import './monacoFileEditor.css';
import '../codeEditor/codeEditor.css';

export type MonacoFileEditorMode = 'text' | 'code';

export interface MonacoFileEditorProps {
  mode?: MonacoFileEditorMode;
  filesLinkTo?: string;
  filesLinkLabel?: string;
  chrome?: EditorChromeMode;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error' | 'locked';

const JENC_CONTENT_TYPE = 'application/vnd.jarvis.encrypted+json; charset=utf-8';
const LARGE_FILE_CHARS = 1_500_000;
const DIAGNOSTICS_OWNER = 'jarvis-code-editor';

function resolveMonacoTheme(): 'vs' | 'vs-dark' {
  if (typeof document === 'undefined') return 'vs';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'vs-dark' : 'vs';
}

async function showRevisionConflictDialog(): Promise<'reload' | 'copy' | 'cancel'> {
  const dialog = custom({
    title: 'Revision conflict',
    messageHtml:
      '<p style="margin:0;line-height:1.45">The file changed on the server since you opened it. Reload the latest version or save your edits as a copy.</p>',
    buttons: [
      {
        text: 'Reload',
        type: 'default',
        stylingMode: 'contained',
        onClick: () => 'reload' as const
      },
      {
        text: 'Save as copy',
        stylingMode: 'outlined',
        onClick: () => 'copy' as const
      },
      {
        text: 'Cancel',
        stylingMode: 'text',
        onClick: () => 'cancel' as const
      }
    ]
  });

  try {
    const result = await dialog.show();
    if (result === 'reload' || result === 'copy' || result === 'cancel') return result;
    return 'cancel';
  } catch {
    return 'cancel';
  }
}

function detectEol(text: string): 'LF' | 'CRLF' {
  return text.includes('\r\n') ? 'CRLF' : 'LF';
}

export function MonacoFileEditor({
  mode = 'text',
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser',
  chrome: chromeProp
}: MonacoFileEditorProps) {
  const isCode = mode === 'code';
  const productName = isCode ? 'Code Editor' : 'Text Editor';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mountId = (params.get('mountId') ?? '').trim();
  const path = (params.get('path') ?? '').trim();
  const viewOnly = (params.get('mode') ?? '').trim().toLowerCase() === 'view';
  const hasSource = Boolean(mountId && path);
  const embedded = isEmbeddedEditor(params);
  const chrome = resolveEditorChromeMode(params, chromeProp);

  useShellToolbarCenter(null);

  const [loadState, setLoadState] = useState<LoadState>(hasSource ? 'loading' : 'idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [monacoReady, setMonacoReady] = useState(false);
  const [monacoError, setMonacoError] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');
  const [revision, setRevision] = useState<string | null>(null);
  const [editorValue, setEditorValue] = useState('');
  const [savedValue, setSavedValue] = useState('');
  const [wordWrap, setWordWrap] = useState(!isCode);
  const [saving, setSaving] = useState(false);
  const [monacoTheme, setMonacoTheme] = useState(resolveMonacoTheme);
  const [unlockVisible, setUnlockVisible] = useState(false);
  const [jencLockKind, setJencLockKind] = useState<JencLockKind>('none');
  const [cryptoStatus, setCryptoStatus] = useState<EditorCryptoStatus | null>(null);
  const [hasManagedWrappedKey, setHasManagedWrappedKey] = useState(false);
  const [cursor, setCursor] = useState({ line: 1, column: 1 });
  const [diagnostics, setDiagnostics] = useState<CodeDiagnostic[]>([]);
  const [problemsOpen, setProblemsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [prefs, setPrefs] = useState<CodeEditorPreferences>(() => loadCodeEditorPreferences());
  const [languageOverride, setLanguageOverride] = useState<string | null>(null);
  const [lspHealth, setLspHealth] = useState<LanguageServiceHealthDto | null>(null);

  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  const revisionRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const pathRef = useRef(path);
  const mountIdRef = useRef(mountId);
  const encryptedRef = useRef(false);
  const passphraseRef = useRef('');
  const rawEnvelopeRef = useRef<string | null>(null);
  const prefsRef = useRef(prefs);

  pathRef.current = path;
  mountIdRef.current = mountId;
  revisionRef.current = revision;
  prefsRef.current = prefs;

  const detectedLanguage = useMemo(() => languageFromPath(path), [path]);
  const language = languageOverride ?? detectedLanguage;
  const capability = useMemo(() => getLanguageCapability(language), [language]);
  const title = path ? displayNameFromPath(path) : '';
  const dirty = !viewOnly && loadState === 'loaded' && editorValue !== savedValue;
  const largeFile = editorValue.length >= LARGE_FILE_CHARS;
  const effective = useMemo(() => {
    if (!isCode) return null;
    // Touch prefs so option changes recompute (getEffectiveOptions reads storage).
    void prefs;
    return getEffectiveOptions(language);
  }, [isCode, language, prefs]);
  const formatEnabled = isCode
    ? capability.capabilities.formatter
    : language === 'json' || language === 'xml';
  const eol = useMemo(() => detectEol(editorValue), [editorValue]);
  const intelligenceLabel = useMemo(() => {
    if (!isCode) return '';
    if (largeFile) return 'large-file';
    if (!shouldUseMonacoOnlyFallback(lspHealth) && capability.source === 'server-lsp') {
      return 'server-lsp';
    }
    return capability.source;
  }, [capability.source, isCode, largeFile, lspHealth]);

  const errorCount = diagnostics.filter((d) => d.severity === 'error').length;
  const warningCount = diagnostics.filter((d) => d.severity === 'warning').length;

  const applyLoadedContent = useCallback((payload: StorageContentResponse) => {
    const lockKind = resolveJencLockKind(payload.path, payload.content);
    const encrypted = lockKind !== 'none';
    const hasEnvelope = lockKind === 'existing';
    encryptedRef.current = encrypted;
    rawEnvelopeRef.current = hasEnvelope ? payload.content : null;
    setJencLockKind(lockKind);
    setHasManagedWrappedKey(Boolean(parseEnvelope(payload.content)?.managedWrappedKey));
    setContentType(payload.contentType || (encrypted ? JENC_CONTENT_TYPE : ''));
    setRevision(payload.revision);

    if (encrypted) {
      setEditorValue('');
      setSavedValue('');
      setLoadState('locked');
      setUnlockVisible(true);
      setLoadError(null);
      return;
    }

    setEditorValue(payload.content);
    setSavedValue(payload.content);
    setLoadState('loaded');
    setLoadError(null);
  }, []);

  const reloadContent = useCallback(async () => {
    if (!mountIdRef.current || !pathRef.current) return;
    setLoadState('loading');
    setLoadError(null);
    try {
      const payload = await fetchContent(mountIdRef.current, pathRef.current);
      applyLoadedContent(payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load file.';
      setLoadState('error');
      setLoadError(message);
      showErrorToast(message);
    }
  }, [applyLoadedContent]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await fetchEditorCryptoStatus();
        if (!cancelled) setCryptoStatus(status);
      } catch {
        if (!cancelled) {
          setCryptoStatus({
            managedRecoveryAvailable: false,
            pbkdf2Iterations: 480_000,
            magic: 'JARVIS-ENC/1'
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isCode) return;
    let cancelled = false;
    void fetchCodeLanguageServiceHealth()
      .then((health) => {
        if (!cancelled) setLspHealth(health);
      })
      .catch(() => {
        if (!cancelled) setLspHealth(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isCode]);

  useEffect(() => {
    let cancelled = false;
    void ensureMonacoReady()
      .then(() => {
        if (!cancelled) {
          setMonacoReady(true);
          setMonacoError(null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setMonacoReady(false);
          setMonacoError(
            error instanceof Error ? error.message : `Failed to initialize the ${productName.toLowerCase()} runtime.`
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [productName]);

  useEffect(() => {
    if (!isCode || !effective) return;
    setWordWrap(effective.wordWrap === 'on' || effective.wordWrap === 'bounded');
  }, [effective, isCode]);

  const handleUnlock = useCallback(
    async (passphrase: string, useManagedRecovery: boolean) => {
      const envelopeContent = rawEnvelopeRef.current;

      if (!envelopeContent || !detectEncryptedContent(envelopeContent)) {
        if (useManagedRecovery) {
          throw new Error('Managed recovery requires an existing encrypted envelope.');
        }
        if (!passphrase.trim()) throw new Error('Passphrase is required.');
        passphraseRef.current = passphrase;
        setEditorValue('');
        setSavedValue('');
        setLoadState('loaded');
        setUnlockVisible(false);
        setLoadError(null);
        return;
      }

      let plaintext: string;
      if (useManagedRecovery) {
        const envelope = parseEnvelope(envelopeContent);
        if (!envelope?.managedWrappedKey) {
          throw new Error('This file does not include a managed recovery key.');
        }
        const { dataKeyB64 } = await managedUnwrapDataKey({
          path: pathRef.current,
          mountId: mountIdRef.current,
          wrappedKeyB64: envelope.managedWrappedKey
        });
        plaintext = await decryptEnvelopeWithRawKey(envelopeContent, contentKeyFromBase64(dataKeyB64));
      } else {
        plaintext = await decryptEnvelope(envelopeContent, passphrase);
        passphraseRef.current = passphrase;
      }

      setEditorValue(plaintext);
      setSavedValue(plaintext);
      setLoadState('loaded');
      setUnlockVisible(false);
      setLoadError(null);
    },
    []
  );

  const handleUnlockCancel = useCallback(() => {
    setUnlockVisible(false);
    setLoadState((current) => resolveUnlockCancelLoadState(current));
    setLoadError(null);
  }, []);

  const buildPersistContent = useCallback(
    async (plaintext: string): Promise<{ content: string; contentType?: string }> => {
      if (!encryptedRef.current) {
        return { content: plaintext, contentType: contentType || undefined };
      }

      const passphrase = passphraseRef.current;
      if (!passphrase) {
        throw new Error('Passphrase is required to save an encrypted file.');
      }

      const iterations = cryptoStatus?.pbkdf2Iterations ?? 480_000;
      let encryptedContent = await encryptPlaintext(plaintext, passphrase, { pbkdf2Iterations: iterations });

      if (cryptoStatus?.managedRecoveryAvailable) {
        const envelope = parseEnvelope(encryptedContent);
        if (envelope) {
          const keyBytes = await exportContentKeyBytes(passphrase, envelope);
          const wrapped = await managedWrapDataKey({
            path: pathRef.current,
            mountId: mountIdRef.current,
            dataKeyB64: contentKeyToBase64(keyBytes)
          });
          encryptedContent = withManagedWrappedKey(encryptedContent, wrapped.wrappedKeyB64);
        }
      }

      rawEnvelopeRef.current = encryptedContent;
      setHasManagedWrappedKey(Boolean(parseEnvelope(encryptedContent)?.managedWrappedKey));
      return { content: encryptedContent, contentType: JENC_CONTENT_TYPE };
    },
    [contentType, cryptoStatus]
  );

  useEffect(() => {
    if (!hasSource) {
      setLoadState('idle');
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoadState('loading');
    setLoadError(null);

    void (async () => {
      try {
        const payload = await fetchContent(mountId, path);
        if (cancelled) return;
        applyLoadedContent(payload);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiHttpError && isFileAccessRestrictedStatus(error.status)) {
          navigate(fileAccessRestrictedHref(), { replace: true });
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load file.';
        setLoadState('error');
        setLoadError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyLoadedContent, hasSource, mountId, navigate, path]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title ? `${title} — ${productName}` : `JARVIS ${productName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [productName, title]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setMonacoTheme(resolveMonacoTheme());
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const applyDiagnostics = useCallback(
    (text: string, languageId: string) => {
      if (!isCode || largeFile || !capability.capabilities.validator) {
        setDiagnostics([]);
        const model = editorRef.current?.getModel();
        if (model) monaco.editor.setModelMarkers(model, DIAGNOSTICS_OWNER, []);
        return;
      }
      const next = validateCodeContent(languageId, text);
      setDiagnostics(next);
      const model = editorRef.current?.getModel();
      if (!model) return;
      monaco.editor.setModelMarkers(
        model,
        DIAGNOSTICS_OWNER,
        next.map((d) => ({
          severity: toMonacoMarkerSeverity(d.severity),
          message: d.message,
          startLineNumber: d.line,
          startColumn: d.column,
          endLineNumber: d.line,
          endColumn: d.column + 1,
          source: d.source
        }))
      );
    },
    [capability.capabilities.validator, isCode, largeFile]
  );

  useEffect(() => {
    if (!isCode || loadState !== 'loaded') return;
    if (!prefs.lintOnChange) return;
    const timer = window.setTimeout(() => {
      applyDiagnostics(editorValue, language);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [applyDiagnostics, editorValue, isCode, language, loadState, prefs.lintOnChange]);

  const runFormat = useCallback(async (): Promise<boolean> => {
    const current = editorRef.current?.getValue() ?? editorValue;
    try {
      if (isCode) {
        const editor = editorRef.current;
        if (capability.source === 'monaco-builtin' && editor) {
          await editor.getAction('editor.action.formatDocument')?.run();
          const formatted = editor.getValue();
          setEditorValue(formatted);
          return true;
        }
        const formatted = formatCodeContent(language, current);
        setEditorValue(formatted);
        editorRef.current?.setValue(formatted);
        return true;
      }
      const formatted = language === 'json' ? formatJson(current) : formatXml(current);
      setEditorValue(formatted);
      editorRef.current?.setValue(formatted);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not format content.';
      showErrorToast(message);
      return false;
    }
  }, [capability.source, editorValue, isCode, language]);

  const persist = useCallback(
    async (options?: { targetPath?: string; skipRevisionCheck?: boolean; silent?: boolean }) => {
      const activeMountId = mountIdRef.current;
      const activePath = options?.targetPath ?? pathRef.current;
      if (!activeMountId || !activePath || savingRef.current) return false;

      savingRef.current = true;
      setSaving(true);
      try {
        if (isCode && prefsRef.current.formatOnSave && !largeFile) {
          await runFormat();
        }
        const plaintext = editorRef.current?.getValue() ?? editorValue;
        const payload = await buildPersistContent(plaintext);
        const result = await saveContent(activeMountId, {
          path: activePath,
          content: payload.content,
          contentType: payload.contentType,
          expectedRevision:
            options?.skipRevisionCheck || options?.targetPath ? undefined : revisionRef.current ?? undefined
        });
        setRevision(result.revision);
        const nextValue = editorRef.current?.getValue() ?? editorValue;
        setSavedValue(nextValue);
        if (options?.targetPath && options.targetPath !== pathRef.current) {
          setParams(
            {
              mountId: activeMountId,
              path: result.path
            },
            { replace: true }
          );
        }
        if (!options?.silent) {
          showSuccessToast(options?.targetPath ? 'Saved as copy.' : 'Saved.');
        }
        return true;
      } catch (error) {
        if (error instanceof ContentRevisionConflictError) {
          showWarningToast('Revision conflict — choose reload or save as copy.');
          const choice = await showRevisionConflictDialog();
          if (choice === 'reload') {
            await reloadContent();
          } else if (choice === 'copy') {
            const copyPath = copyPathForConflict(pathRef.current);
            await persist({ targetPath: copyPath, skipRevisionCheck: true });
          }
          return false;
        }

        const message = error instanceof Error ? error.message : 'Save failed.';
        if (error instanceof ApiHttpError && isFileAccessRestrictedStatus(error.status)) {
          navigate(fileAccessRestrictedHref(), { replace: true });
          return false;
        }
        showErrorToast(message);
        return false;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [buildPersistContent, editorValue, isCode, largeFile, navigate, reloadContent, runFormat, setParams]
  );

  const onSave = useCallback(() => {
    void persist();
  }, [persist]);

  const onFormat = useCallback(() => {
    void runFormat();
  }, [runFormat]);

  const onValidate = useCallback(() => {
    applyDiagnostics(editorRef.current?.getValue() ?? editorValue, language);
    setProblemsOpen(true);
  }, [applyDiagnostics, editorValue, language]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void persist();
      }
      if (isCode && (event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        void editorRef.current?.getAction('editor.action.quickCommand')?.run();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCode, persist]);

  useEffect(() => {
    if (!dirty || loadState !== 'loaded') return;
    const autosaveMs = getEditorAutosaveIntervalMs();
    if (!autosaveMs) return;
    const timer = window.setInterval(() => {
      void persist({ silent: true });
    }, autosaveMs);
    return () => window.clearInterval(timer);
  }, [dirty, loadState, persist]);

  const onMount: OnMount = (editor) => {
    editorRef.current = editor;
    const wrap = isCode && effective ? effective.wordWrap : wordWrap ? 'on' : 'off';
    editor.updateOptions({
      wordWrap: typeof wrap === 'string' ? wrap : wordWrap ? 'on' : 'off',
      ...(isCode && effective
        ? {
            fontSize: effective.fontSize,
            fontFamily: effective.fontFamily,
            tabSize: effective.tabSize,
            insertSpaces: effective.insertSpaces,
            minimap: { enabled: effective.minimap && !largeFile },
            renderWhitespace: effective.renderWhitespace,
            bracketPairColorization: { enabled: !largeFile },
            folding: true,
            quickSuggestions: !largeFile && capability.capabilities.completion
          }
        : {})
    });
    editor.onDidChangeCursorPosition((e) => {
      setCursor({ line: e.position.lineNumber, column: e.position.column });
    });
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (isCode && effective) {
      editorRef.current.updateOptions({
        wordWrap: effective.wordWrap,
        fontSize: effective.fontSize,
        fontFamily: effective.fontFamily,
        tabSize: effective.tabSize,
        insertSpaces: effective.insertSpaces,
        minimap: { enabled: effective.minimap && !largeFile },
        renderWhitespace: effective.renderWhitespace,
        bracketPairColorization: { enabled: !largeFile },
        quickSuggestions: !largeFile && capability.capabilities.completion
      });
    } else {
      editorRef.current.updateOptions({ wordWrap: wordWrap ? 'on' : 'off' });
    }
  }, [capability.capabilities.completion, effective, isCode, largeFile, wordWrap]);

  const persistPrefs = useCallback((next: CodeEditorPreferences) => {
    setPrefs(next);
    saveCodeEditorPreferences(next);
  }, []);

  const languageItems = useMemo(
    () =>
      listLanguageCapabilities().map((entry) => ({
        id: entry.monacoLanguageId,
        text: entry.displayName
      })),
    []
  );

  if (!hasSource) {
    const onCreatedFile = (created: { mountId: string; relativePath: string }) => {
      setParams(
        {
          mountId: created.mountId,
          path: created.relativePath
        },
        { replace: true }
      );
    };

    if (isCode) {
      return (
        <div className="ops-fill-page ops-fill-page--bare text-editor-page code-editor-page">
          <CodeEditorCreatePanel
            intro={
              <>
                Create a new code or config file in My Storage, or open an existing file from{' '}
                <Link to={filesLinkTo}>{filesLinkLabel}</Link>.
              </>
            }
            secondaryAction={
              <Link to={filesLinkTo} className="dx-link">
                Browse {filesLinkLabel}
              </Link>
            }
            onCreated={onCreatedFile}
          />
        </div>
      );
    }

    return (
      <div className="ops-fill-page ops-fill-page--bare text-editor-page">
        <TextEditorCreatePanel
          intro={
            <>
              Create a plain text, encrypted, log, or env file in My Storage, or open an existing file
              from <Link to={filesLinkTo}>{filesLinkLabel}</Link>.
            </>
          }
          secondaryAction={
            <Link to={filesLinkTo} className="dx-link">
              Browse {filesLinkLabel}
            </Link>
          }
          onCreated={onCreatedFile}
        />
      </div>
    );
  }

  const header = (
    <header className="text-editor-page__header" aria-label={`${productName} toolbar`}>
      {chrome === 'full' ? (
        <Link to={filesLinkTo} className="dx-link">
          {filesLinkLabel}
        </Link>
      ) : null}
      <span className="text-editor-page__title" title={title}>
        {title}
      </span>
      <div className="text-editor-page__toolbar" role="toolbar" aria-label="Editor actions">
        {!viewOnly ? (
          <>
            <Button
              icon="save"
              text="Save"
              stylingMode="contained"
              type="default"
              disabled={loadState !== 'loaded' || saving || !dirty}
              onClick={onSave}
            />
            <Button
              icon="orderedlist"
              text="Format"
              stylingMode="text"
              hint={isCode ? describeSupport(language) : 'Pretty-print JSON or XML'}
              disabled={!formatEnabled || loadState !== 'loaded' || saving || largeFile}
              onClick={onFormat}
            />
          </>
        ) : null}
        {isCode ? (
          <>
            <Button
              icon="warning"
              text="Validate"
              stylingMode="text"
              disabled={!capability.capabilities.validator || loadState !== 'loaded' || largeFile}
              onClick={onValidate}
            />
            <Button
              icon="bulletlist"
              text="Problems"
              stylingMode="text"
              disabled={loadState !== 'loaded'}
              onClick={() => setProblemsOpen((v) => !v)}
            />
            <SelectBox
              dataSource={languageItems}
              value={language}
              valueExpr="id"
              displayExpr="text"
              width={150}
              stylingMode="outlined"
              searchEnabled
              disabled={loadState !== 'loaded'}
              onValueChanged={(e) => setLanguageOverride(String(e.value))}
            />
            <Button
              icon="preferences"
              text="Settings"
              stylingMode="text"
              onClick={() => setSettingsOpen(true)}
            />
            <Button icon="help" stylingMode="text" hint="Support matrix" onClick={() => setHelpOpen(true)} />
            <Button
              icon="overflow"
              text="Commands"
              stylingMode="text"
              disabled={loadState !== 'loaded'}
              onClick={() => void editorRef.current?.getAction('editor.action.quickCommand')?.run()}
            />
          </>
        ) : null}
        <Button
          icon="alignleft"
          text={wordWrap ? 'Wrap: on' : 'Wrap: off'}
          stylingMode="text"
          disabled={loadState !== 'loaded'}
          onClick={() => {
            setWordWrap((value) => !value);
            if (isCode) {
              persistPrefs({
                ...prefs,
                wordWrap: !wordWrap ? 'on' : 'off'
              });
            }
          }}
        />
      </div>
      <div className="text-editor-page__meta" aria-live="polite">
        <span className="text-editor-page__badge">{language}</span>
        {viewOnly ? <span className="text-editor-page__badge">View only</span> : null}
        {dirty ? <span className="text-editor-page__dirty">Unsaved changes</span> : null}
        {saving ? <LoadIndicator height={18} width={18} visible /> : null}
        {loadState === 'error' && loadError ? (
          <span className="text-editor-page__badge text-editor-page__badge--error" role="alert">
            {loadError}
          </span>
        ) : null}
      </div>
    </header>
  );

  const stage = (
    <div className="text-editor-page__stage">
      {monacoError ? (
        <div className="text-editor-page__empty" role="alert">
          <p>{monacoError}</p>
        </div>
      ) : loadState === 'loading' || !monacoReady ? (
        <div className="text-editor-page__empty" aria-busy="true">
          <LoadIndicator height={36} width={36} visible />
          <p>{loadState === 'loading' ? 'Loading file…' : 'Starting editor…'}</p>
        </div>
      ) : loadState === 'locked' ? (
        <div className="text-editor-page__empty" aria-busy={unlockVisible}>
          <p>
            {jencLockKind === 'new'
              ? 'Set a passphrase to protect this encrypted file.'
              : 'Unlock the encrypted file to begin editing.'}
          </p>
          {!unlockVisible ? (
            <Button
              text={jencLockKind === 'new' ? 'Set passphrase' : 'Unlock'}
              stylingMode="contained"
              type="default"
              onClick={() => setUnlockVisible(true)}
            />
          ) : null}
        </div>
      ) : loadState === 'error' ? (
        <div className="text-editor-page__empty" role="alert">
          <p>{loadError ?? 'Failed to load file.'}</p>
          <Button text="Retry" stylingMode="contained" onClick={() => void reloadContent()} />
        </div>
      ) : (
        <div className="text-editor-page__monaco">
          <Editor
            key={`${mountId}:${path}:${monacoTheme}:${language}`}
            language={language}
            theme={monacoTheme}
            value={editorValue}
            onChange={(value) => {
              if (!viewOnly) setEditorValue(value ?? '');
            }}
            onMount={onMount}
            loading={
              <div className="text-editor-page__empty" aria-busy="true">
                <LoadIndicator height={36} width={36} visible />
                <p>Starting editor…</p>
              </div>
            }
            options={{
              automaticLayout: true,
              minimap: { enabled: Boolean(isCode && effective?.minimap && !largeFile) },
              scrollBeyondLastLine: false,
              wordWrap: isCode && effective ? effective.wordWrap : wordWrap ? 'on' : 'off',
              fontSize: isCode && effective ? effective.fontSize : 14,
              fontFamily: isCode && effective ? effective.fontFamily : undefined,
              tabSize: isCode && effective ? effective.tabSize : 2,
              insertSpaces: isCode && effective ? effective.insertSpaces : true,
              renderWhitespace: isCode && effective ? effective.renderWhitespace : 'selection',
              bracketPairColorization: { enabled: isCode && !largeFile },
              multiCursorModifier: 'ctrlCmd',
              fixedOverflowWidgets: true,
              readOnly: viewOnly
            }}
          />
        </div>
      )}
      {isCode && problemsOpen ? (
        <aside className="code-editor-page__problems" aria-label="Problems">
          <div className="code-editor-page__problems-header">
            <strong>Problems</strong>
            <span>
              {errorCount} errors · {warningCount} warnings
            </span>
            <Button icon="close" stylingMode="text" onClick={() => setProblemsOpen(false)} />
          </div>
          {largeFile ? (
            <p className="code-editor-page__problems-empty">
              Large-file mode: expensive diagnostics are disabled so editing stays responsive.
            </p>
          ) : diagnostics.length === 0 ? (
            <p className="code-editor-page__problems-empty">No problems in browser validation.</p>
          ) : (
            <ul className="code-editor-page__problems-list">
              {diagnostics.map((d, index) => (
                <li key={`${d.line}:${d.column}:${index}`}>
                  <button
                    type="button"
                    className={`code-editor-page__problem code-editor-page__problem--${d.severity}`}
                    onClick={() => {
                      editorRef.current?.revealPositionInCenter({
                        lineNumber: d.line,
                        column: d.column
                      });
                      editorRef.current?.setPosition({ lineNumber: d.line, column: d.column });
                      editorRef.current?.focus();
                    }}
                  >
                    <span>{d.severity}</span>
                    <span>
                      Ln {d.line}, Col {d.column}
                    </span>
                    <span>{d.message}</span>
                    <span>{d.source}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
      ) : null}
    </div>
  );

  const statusBar = isCode ? (
    <footer className="code-editor-page__status" aria-label="Code editor status">
      <span>{dirty ? 'Unsaved' : saving ? 'Saving…' : 'Saved'}</span>
      <span>
        {errorCount} ✕ · {warningCount} ⚠
      </span>
      <span>
        Ln {cursor.line}, Col {cursor.column}
      </span>
      <span>
        {effective?.insertSpaces ? `Spaces: ${effective.tabSize}` : `Tabs: ${effective?.tabSize ?? 2}`}
      </span>
      <span>UTF-8</span>
      <span>{eol}</span>
      <span title={describeSupport(language)}>{capability.displayName}</span>
      <span title="Active intelligence provider">{intelligenceLabel}</span>
      {largeFile ? <span className="code-editor-page__status-warn">Large-file mode</span> : null}
    </footer>
  ) : null;

  const pageBody = (
    <>
      {header}
      {stage}
      {statusBar}
      {isCode ? (
        <>
          <Popup
            visible={settingsOpen}
            onHiding={() => setSettingsOpen(false)}
            title="Code Editor settings"
            showCloseButton
            width={420}
            height="auto"
          >
            <div className="code-editor-page__settings">
              <label>
                Font size
                <NumberBox
                  value={prefs.fontSize}
                  min={10}
                  max={32}
                  onValueChanged={(e) => persistPrefs({ ...prefs, fontSize: Number(e.value) || 14 })}
                />
              </label>
              <label>
                Tab size
                <NumberBox
                  value={prefs.tabSize}
                  min={1}
                  max={8}
                  onValueChanged={(e) => persistPrefs({ ...prefs, tabSize: Number(e.value) || 2 })}
                />
              </label>
              <CheckBox
                text="Insert spaces"
                value={prefs.insertSpaces}
                onValueChanged={(e) => persistPrefs({ ...prefs, insertSpaces: Boolean(e.value) })}
              />
              <CheckBox
                text="Minimap"
                value={prefs.minimap}
                onValueChanged={(e) => persistPrefs({ ...prefs, minimap: Boolean(e.value) })}
              />
              <CheckBox
                text="Format on save"
                value={prefs.formatOnSave}
                onValueChanged={(e) => persistPrefs({ ...prefs, formatOnSave: Boolean(e.value) })}
              />
              <CheckBox
                text="Lint on change"
                value={prefs.lintOnChange}
                onValueChanged={(e) => persistPrefs({ ...prefs, lintOnChange: Boolean(e.value) })}
              />
              <p className="code-editor-page__settings-note">
                Diagnostics warn but never block save. Incomplete code remains editable and savable.
              </p>
            </div>
          </Popup>
          <Popup
            visible={helpOpen}
            onHiding={() => setHelpOpen(false)}
            title="Code Editor support matrix"
            showCloseButton
            width={560}
            height={480}
          >
            <div className="code-editor-page__help">
              <p>
                Browser IntelliSense uses Monaco workers where available. Project-aware LSP adapters are
                gated behind the secure language-service gateway and fall back to Monaco-only when
                unavailable.
              </p>
              <p>Content limit for language services is below the 32 MiB text-content API cap.</p>
              <ul>
                {listLanguageCapabilities().map((entry) => (
                  <li key={entry.monacoLanguageId}>
                    <strong>{entry.displayName}</strong> — {describeSupport(entry.monacoLanguageId)}
                  </li>
                ))}
              </ul>
            </div>
          </Popup>
        </>
      ) : null}
    </>
  );

  return (
    <div className={`ops-fill-page ops-fill-page--bare text-editor-page${isCode ? ' code-editor-page' : ''}`}>
      <JencUnlockDialog
        visible={unlockVisible}
        fileName={title || 'Encrypted file'}
        mode={jencLockKind === 'new' ? 'set-passphrase' : 'unlock'}
        managedRecoveryAvailable={cryptoStatus?.managedRecoveryAvailable ?? false}
        hasManagedWrappedKey={hasManagedWrappedKey}
        onUnlock={handleUnlock}
        onCancel={handleUnlockCancel}
      />
      {chrome === 'minimal' && !embedded ? (
        <MinimalEditorChrome title={title || productName}>{pageBody}</MinimalEditorChrome>
      ) : (
        pageBody
      )}
    </div>
  );
}
